import { IRole } from '~/store/user/types';
import { IDictionary } from '../dictionaries/types';

export interface ICrisisAllocation {
  id: number;
  isArchived: boolean;
  isOwn: boolean;
  date: string;
  team: IDictionary;
  locationGroups: IDictionary[];
  user: {
    id: number;
    name: string;
    photo: string;
    photoThumbnail: string;
    roles: IRole[];
    subRoles: IDictionary[];
    clients: [
      {
        id: number;
        firstName: string;
        lastName: string;
        photo: string;
        photoThumbnail: string;
        dateOfBirth: string;
        medicaId: string;
        isArchived: boolean;
        status: string;
        memo: string;
        gender: IDictionary;
        coordinates: {
          lat: number;
          lng: number;
        };
      },
    ];
  };
}

export interface IAddCrisisAllocationPayload {
  date: string;
  crisisCallId?: number;
  clientId?: number;
  memo: string;
  userIds: number[];
}

export interface IManualCrisisAllocationPayload {
  clientId: number;
  date: string;
  memo: string;
  userIds: number[];
}

export type TCrisisAddress = {
  shelterName?: string;
  city: string;
  zipCode: string;
  poBox?: string;
  line1: string;
  line2: string;
  lat: number;
  lng: number;
  state: {
    id: number;
    name: string;
    abbreviation: string;
  };
};

type TCrisisClient = {
  id: number;
  firstName: string;
  lastName: string;
  emrMrn: string;
  photo: string;
  photoThumbnail: string;
  teams: [
    {
      id: number;
      team: IDictionary;
      isPrimary: boolean;
    },
  ];
};
export interface ICrisisCall {
  receivedOn: string;
  documentId: IDictionary;
  client: TCrisisClient;
  address: TCrisisAddress;
  crisisCallId: number;
  isNewCrisisCall?: boolean;
}

interface ICrisisCallInfo {
  crisisCallId: number;
  receivedOn: string;
  documentId: {
    id: number;
    name: string;
  };
  client: {
    id: number;
    firstName: string;
    lastName: string;
    emrMrn: string;
    photo: string;
    photoThumbnail: string;
    medicaidId: string | null;
    gender: {
      id: number;
      name: string;
    };
    dateOfBirth: string;
    teams: [
      {
        id: number;
        team: {
          id: number;
          name: string;
        };
        isPrimary: boolean;
      },
    ];
  };
  address: {
    shelterName: string | null;
    city: string;
    zipCode: string;
    line1: string;
    line2: string | null;
    lat: number;
    lng: number;
    state: {
      abbreviation: string;
      id: number;
      name: string;
    };
  };
}

export interface ICrisisCallsAllocatedClients {
  id: number;
  allocatedTeamMembers: string[];
  allocatedTime: string;
  visitStartTime: string | null;
  crisisCallInfo: ICrisisCallInfo;
  isDispatch: boolean;
}

export interface ICrisisCallsAllocatedClientsList {
  allocationClientId?: number;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientMrn: string;
  clientPhoto: string;
  isDispatch: boolean;
  allocatedTeamMembers: string[];
  clientPhotoThumbnail: string;
  crisisCallId: number;
  address: {
    shelterName: string;
    city: string;
    zipCode: string;
    line1: string;
    line2: string;
    lat: number;
    lng: number;
    state: {
      id: number;
      name: string;
      abbreviation: string;
    };
  };
  allocationDate: string;
  visitStartTime: string;
}

export enum CrisisStatus {
  UNALLOCATED = 'Unallocated',
  ALLOCATED = 'Allocated',
  SERVICE_STARTED = 'ServiceStarted',
}

export interface ICrisisCallTeamMember {
  id: number;
  firstName: string;
  lastName: string;
  photo: string;
  photoThumbnail: string;
  crisisStatus?: CrisisStatus;
  allocatedClients: ICrisisCallsAllocatedClientsList[];
}

export interface IAllocationsDeletedEvent {
  allocationClientIds?: number[];
}

export interface ICrisisCallIdEvent {
  crisisCallId?: number;
}

export interface IVisitStartedEvent {
  userId?: number;
  clientId?: number;
  crisisCallId?: number;
  startTimeUtc?: string;
}

export interface IVisitFinishedEvent {
  userId?: number;
  clientId?: number;
  crisisCallId?: number;
  isCanceled?: boolean;
}

export interface IVisitCanceledEvent {
  userId?: number;
  clientId?: number;
  crisisCallId?: number;
}

export interface IAllocationsUpdatedEvent {
  createdAllocations?: ICrisisCallTeamMember[];
  deletedClientAllocationsId?: number[];
}

export interface IEditCrisisAddressPayload {
  typeId: number;
  city: string;
  zipCode: string;
  line1: string;
  stateId: number;
  line2: string;
  poBox: string;
  shelterName: string;
}

export interface IEditManualCrisisAllocationAddressPayload extends IEditCrisisAddressPayload {
  clientId: number;
}

export interface IResolveCrisisCallPayload {
  clinicId: number;
  crisisCallId: number;
  tab: number;
  memo: string;
}
